import { useEffect, useState } from "react";
import { ActionIcon, Button, Container, Group, Paper, Stack, Text, TextInput, Title } from "@mantine/core";
import { IconCheck, IconPencil, IconPlus, IconTrash, IconX } from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import HttpClient from "../helpers/HttpClient";
import { StorageLocation } from "../types/inventory.type";

const client = () => new HttpClient<StorageLocation>("/inventory/location");

// Where inventory articles can be found (e.g. "Materialraum", "Kabine 2").
function StorageLocationList() {
  const [locations, setLocations] = useState<StorageLocation[]>([]);
  const [editingId, setEditingId] = useState<number | "new" | null>(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");

  const load = () =>
    client().getAll().then(setLocations).catch(() => notifications.show({ color: "red", message: "Lagerorte konnten nicht geladen werden." }));

  useEffect(() => { load(); }, []);

  const startEdit = (loc: StorageLocation | null) => {
    setEditingId(loc ? loc.id : "new");
    setName(loc?.name ?? "");
    setDescription(loc?.description ?? "");
  };

  const save = async () => {
    if (!name.trim()) return;
    try {
      await client().post({
        ...(editingId !== "new" && editingId ? { id: editingId } : {}),
        name: name.trim(),
        description: description.trim() || undefined,
      } as StorageLocation);
      setEditingId(null);
      await load();
    } catch {
      notifications.show({ color: "red", message: "Speichern fehlgeschlagen." });
    }
  };

  const remove = async (loc: StorageLocation) => {
    if (!confirm(`Lagerort „${loc.name}“ löschen? Artikel an diesem Ort verlieren ihre Ortsangabe.`)) return;
    try {
      await client().delete(loc.id);
      await load();
    } catch {
      notifications.show({ color: "red", message: "Löschen fehlgeschlagen." });
    }
  };

  const editor = (
    <Group gap="xs" wrap="nowrap">
      <TextInput placeholder="Name" value={name} onChange={(e) => setName(e.currentTarget.value)} style={{ flex: 1 }} autoFocus />
      <TextInput placeholder="Beschreibung" value={description} onChange={(e) => setDescription(e.currentTarget.value)} style={{ flex: 2 }} />
      <ActionIcon color="green" variant="light" onClick={save} disabled={!name.trim()} title="Speichern"><IconCheck size={16} /></ActionIcon>
      <ActionIcon variant="subtle" onClick={() => setEditingId(null)} title="Abbrechen"><IconX size={16} /></ActionIcon>
    </Group>
  );

  return (
    <Container size="md" mt="md">
      <Group justify="space-between" mb="md">
        <Title order={2}>Lagerorte</Title>
        <Button leftSection={<IconPlus size={16} />} onClick={() => startEdit(null)} disabled={editingId !== null}>
          Neuer Lagerort
        </Button>
      </Group>
      <Stack gap="xs">
        {editingId === "new" && <Paper withBorder p="sm">{editor}</Paper>}
        {locations.length === 0 && editingId !== "new" && <Text c="dimmed">Noch keine Lagerorte.</Text>}
        {locations.map((loc) => (
          <Paper key={loc.id} withBorder p="sm">
            {editingId === loc.id ? editor : (
              <Group justify="space-between" wrap="nowrap">
                <div>
                  <Text fw={500}>{loc.name}</Text>
                  {loc.description && <Text size="sm" c="dimmed">{loc.description}</Text>}
                </div>
                <Group gap={4}>
                  <ActionIcon variant="subtle" onClick={() => startEdit(loc)} title="Bearbeiten"><IconPencil size={16} /></ActionIcon>
                  <ActionIcon variant="subtle" color="red" onClick={() => remove(loc)} title="Löschen"><IconTrash size={16} /></ActionIcon>
                </Group>
              </Group>
            )}
          </Paper>
        ))}
      </Stack>
    </Container>
  );
}

export default StorageLocationList;
